import { useState, useEffect } from "react";
import { OwcTypography, OwcIconButton, OwcInput } from "@one/react";
import PropTypes from "prop-types";
import InlineTypography from "./InlineTypography";
import { valueOrEmpty, formatUserDisplay } from "../../../../utils/helpers/text";
import { useSelector } from "react-redux";
import { withApollo } from "react-apollo";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../../../gql/bookingapi/queries";
import { QUICK_EDIT_BOOKING } from "../../../../gql/bookingapi/mutations";
import AssistantSelect from "../../AssistantSelect/AssistantSelect";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { quickEditTracker } from "../../../../features/booking-operations/quickEditTracker";

const formatAssistanceDuration = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") return `${v}h`;
  const num = Number(v);
  if (!isNaN(num)) return `${num}h`;
  return String(v);
};

const AssistanceDetails = ({
  show = true,
  assistanceDeclaration,
  assistanceDuration,
  assistantName,
  approvalStatus,
  client,
  booking,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  onClick,
  allowEdit = false,
  startLoading,
  stopLoading,
  setStatus,
  dispatchAction
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [selectedAssistantName, setSelectedAssistantName] = useState("");
  const [editedDuration, setEditedDuration] = useState("");

  const [originalAssistant, setOriginalAssistant] = useState("");
  const [originalAssistantName, setOriginalAssistantName] = useState("");
  const [originalDuration, setOriginalDuration] = useState("");

  const reduxUsers = useSelector((state) => state.users.users);
  const user = useSelector((store) => store.user);

  useEffect(() => {
    return () => {
      if (assistanceUpdateCallbackRef) {
        assistanceUpdateCallbackRef.current = null;
      }
    };
  }, [assistanceUpdateCallbackRef]);

  const showEditIcon = assistanceDeclaration?.requiresAssistance && approvalStatus === 'APPROVED' && allowEdit;

  useEffect(() => {
    const fetchUsers = async () => {
      if (reduxUsers && reduxUsers.length > 0) {
        setUsers(reduxUsers);
        setFetchingUsers(false);
        return;
      }

      try {
        setFetchingUsers(true);
        const result = await client.query({
          query: LIST_DIGITAL_LAB_BOOKING_USERS,
          fetchPolicy: "network-only",
        });

        if (result?.data?.listDigitalLabBookingUsers?.items) {
          setUsers(result.data.listDigitalLabBookingUsers.items);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setFetchingUsers(false);
      }
    };

    if (showEditIcon || isEditMode) {
      fetchUsers();
    }
  }, [showEditIcon, isEditMode, client, reduxUsers]);

  useEffect(() => {
    if (isEditMode && booking) {
      const duration = assistanceDuration ? assistanceDuration.toString() : "";
      setEditedDuration(duration);
      setOriginalDuration(duration);

      if (booking.assistent && booking.assistent[0] && users.length > 0) {
        const assistantId = booking.assistent[0];
        const assistantUser = users.find(u =>
          u.userId === assistantId ||
          u.name === assistantId ||
          u.username === assistantId
        );
        if (assistantUser) {
          const formattedName = formatUserDisplay(assistantUser);
          setSelectedAssistant(assistantUser.userId);
          setSelectedAssistantName(formattedName);
          setOriginalAssistant(assistantUser.userId);
          setOriginalAssistantName(formattedName);
        } else {
          setSelectedAssistant("");
          setSelectedAssistantName("");
          setOriginalAssistant("");
          setOriginalAssistantName("");
        }
      } else if (assistantName && assistantName !== "Not yet assigned") {
        setSelectedAssistantName(assistantName);
        setOriginalAssistantName(assistantName);
      }
    }
  }, [isEditMode, booking, assistanceDuration, assistantName, users]);

  if (!show) {
    return <></>;
  }

  const hasChanges = () => {
    return selectedAssistant !== originalAssistant ||
      selectedAssistantName !== originalAssistantName ||
      editedDuration !== originalDuration;
  };

  const handleEdit = () => {
    if (!allowEdit && onClick) {
      onClick();
    } else {
      setIsEditMode(true);
    }
  };


  const handleCancel = () => {
    setSelectedAssistant(originalAssistant);
    setSelectedAssistantName(originalAssistantName);
    setEditedDuration(originalDuration);
    setIsEditMode(false);
  };

  const handleSave = async () => {
    if ((!booking?.bookingId && !booking?.id) || !selectedAssistant || !editedDuration) {
      snackbarService.show({
        type: "error",
        message: "Please fill in all required fields",
        duration: 3000,
      });
      return;
    }

    try {
      setSaving(true);

      if (dispatchAction) {
        dispatchAction({ type: "fetching", payload: true });
      }
      if (setStatus) {
        setStatus(true);
      }
      if (startLoading) {
        startLoading();
      }

      if (assistanceUpdateCallbackRef) {
        assistanceUpdateCallbackRef.current = () => {
          setOriginalAssistant(selectedAssistant);
          setOriginalAssistantName(selectedAssistantName);
          setOriginalDuration(editedDuration);
          setIsEditMode(false);
          setSaving(false);

          if (onUpdateComplete) {
            onUpdateComplete();
          }
          if (onAssistanceUpdateComplete) {
            onAssistanceUpdateComplete();
          }
        };
      }

      const sanitizedAssistanceDeclaration = assistanceDeclaration ? {
        requiresAssistance: !!assistanceDeclaration.requiresAssistance,
        assistanceReason: assistanceDeclaration.assistanceReason || null
      } : null;

      const result = await client.mutate({
        mutation: QUICK_EDIT_BOOKING,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            bookingId: booking?.bookingId || booking?.id,
            assistent: [selectedAssistant],
            assistentTime: parseFloat(editedDuration),
            assistanceDeclaration: sanitizedAssistanceDeclaration,
            createdByEmail: user?.email,
            force: true,
          }
        },
      });

      const payload = result?.data?.quickEditBooking;
      if (!payload?.success) {
        throw new Error(payload?.message || 'Failed to update assistance details');
      }

      if (payload?.commandId) {
        quickEditTracker.register(payload.commandId, 'UPDATE', {
          onSuccess: () => {
            if (dispatchAction) {
              dispatchAction({ type: "fetching", payload: false });
            }
            if (setStatus) {
              setStatus(false);
            }
            if (stopLoading) {
              stopLoading();
            }

            setOriginalAssistant(selectedAssistant);
            setOriginalAssistantName(selectedAssistantName);
            setOriginalDuration(editedDuration);
            setIsEditMode(false);
            setSaving(false);

            if (onUpdateComplete) {
              onUpdateComplete();
            }
            if (assistanceUpdateCallbackRef) {
              assistanceUpdateCallbackRef.current = null;
            }
            if (onAssistanceUpdateComplete) {
              onAssistanceUpdateComplete();
            }
          },
          onError: () => {
            if (dispatchAction) {
              dispatchAction({ type: "fetching", payload: false });
            }
            if (setStatus) {
              setStatus(false);
            }
            if (stopLoading) {
              stopLoading();
            }
            setSaving(false);
            if (assistanceUpdateCallbackRef) {
              assistanceUpdateCallbackRef.current = null;
            }
          }
        });
      } else {
        if (dispatchAction) {
          dispatchAction({ type: "fetching", payload: false });
        }
        if (setStatus) {
          setStatus(false);
        }
        if (stopLoading) {
          stopLoading();
        }
        setSaving(false);
      }

      if (onAssistanceUpdateStarted) {
        onAssistanceUpdateStarted();
      }

    } catch (error) {
      console.error("Error updating assistance details:", error);
      snackbarService.show({
        type: "error",
        message: "Failed to update assistance details",
        duration: 3000,
      });
      setSaving(false);

      if (dispatchAction) {
        dispatchAction({ type: "fetching", payload: false });
      }
      if (setStatus) {
        setStatus(false);
      }
      if (stopLoading) {
        stopLoading();
      }
      if (assistanceUpdateCallbackRef) {
        assistanceUpdateCallbackRef.current = null;
      }
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: 'column', width: '100%' }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: '4px', minHeight: '24px', position: 'relative' }}>
        <OwcTypography style={{ width: '70%' }} variant="caption">Assistance details</OwcTypography>
        {showEditIcon && !isEditMode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingTop: '2px' }}>
            <OwcIconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleEdit();
              }}
              style={{ color: '#0B41CD' }}
              icon="edit"
              type="outlined"
              flat={true}
            />
          </div>
        )}
        {showEditIcon && isEditMode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}>
            <OwcIconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleCancel();
              }}
              style={{
                color: hasChanges() ? '#DC3545' : '#999999'
              }}
              icon="circle_clear"
              type="filled"
              flat={true}
              disabled={saving}
              data-testid="assistance-cancel-btn"
            />
            <OwcIconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSave();
              }}
              style={{
                color: hasChanges() && selectedAssistant && editedDuration && parseFloat(editedDuration) > 0 ? '#28A745' : '#999999'
              }}
              icon="circle_confirm"
              type="filled"
              flat={true}
              disabled={saving || !selectedAssistant || !editedDuration || (editedDuration && parseFloat(editedDuration) <= 0) || !hasChanges()}
              data-testid="assistance-save-btn"
            />
          </div>
        )}
      </div>

      {!isEditMode ? (
        <>
          <OwcTypography variant="caption">
            Assistant: <InlineTypography style={{ display: 'inline-block' }} variant="button" element="span">
              {valueOrEmpty(assistantName, false, "Not yet assigned")}
            </InlineTypography>
          </OwcTypography>
          <OwcTypography variant="caption">
            Duration: <InlineTypography variant="button">
              {formatAssistanceDuration(assistanceDuration)}
            </InlineTypography>
          </OwcTypography>
          <OwcTypography variant="caption">
            Reason: <InlineTypography variant="button">
              {assistanceDeclaration?.assistanceReason || "-"}
            </InlineTypography>
          </OwcTypography>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', opacity: saving ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <div>
            <AssistantSelect
              width={250}
              selectedAssistantId={selectedAssistant}
              selectedAssistantName={selectedAssistantName}
              users={users}
              loading={fetchingUsers}
              onAssistantChange={(userId, displayName) => {
                setSelectedAssistant(userId);
                setSelectedAssistantName(displayName);
              }}
              required={true}
              disabled={true} // Quick Edit change assistant disabled for assistant bookings — see Retina-5838182
            />
          </div>

          <div>
            <OwcInput
              type="number"
              required={true}
              label="Duration"
              value={editedDuration}
              disabled={saving}
              min="0.5"
              step="0.5"
              placeholder="Enter hours"
              onInputChange={(e) => {
                setEditedDuration(e.target?.value);
              }}
            >
              <div slot="suffix">h</div>
            </OwcInput>
          </div>

          <div>
            <OwcTypography variant="caption" style={{ color: '#544F4F' }}>
              Reason
            </OwcTypography>
            <OwcTypography variant="body2">
              {assistanceDeclaration?.assistanceReason || "-"}
            </OwcTypography>
          </div>
        </div>
      )}
    </div>
  );
};

AssistanceDetails.propTypes = {
  assistanceDeclaration: PropTypes.shape({
    requiresAssistance: PropTypes.bool,
    assistanceReason: PropTypes.string
  }),
  assistanceDuration: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  assistantName: PropTypes.string,
  approvalStatus: PropTypes.string,
  client: PropTypes.object,
  booking: PropTypes.object,
  onUpdateComplete: PropTypes.func,
  onAssistanceUpdateStarted: PropTypes.func,
  onAssistanceUpdateComplete: PropTypes.func,
  assistanceUpdateCallbackRef: PropTypes.shape({
    current: PropTypes.func
  }),
  onClick: PropTypes.func,
  allowInlineEdit: PropTypes.bool,
  show: PropTypes.bool,
  startLoading: PropTypes.func,
  stopLoading: PropTypes.func,
  setStatus: PropTypes.func,
  dispatchAction: PropTypes.func
};

export default withApollo(AssistanceDetails);
